import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Clock3, Home, LogIn, RefreshCw, ShieldCheck, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { publicApi } from '@/lib/api';

const LOGO_URL = 'https://customer-assets-wrfwihn1.emergentagent.net/job_aac32766-5fa5-4a83-81c9-03bafeaebc9e/artifacts/bi1rz20k_loyerbook.png';

export default function SignupSuccess({ upgrade = false }) {
  const [searchParams] = useSearchParams();
  const intentId = searchParams.get('billing_intent');
  const [status, setStatus] = useState(intentId ? 'checking' : 'missing');
  const [message, setMessage] = useState('Nous vérifions votre paiement…');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!intentId) return undefined;
    let cancelled = false;
    let timer;
    let tries = 0;

    const check = async () => {
      try {
        const response = await publicApi.paymentSync(intentId);
        if (cancelled) return;
        const next = response?.data?.status || 'pending';
        if (next === 'paid') {
          setStatus('paid');
          setMessage(upgrade ? 'Votre abonnement a été renouvelé.' : 'Votre compte LoyerBook est maintenant actif.');
          return;
        }
        if (next === 'failed' || next === 'cancelled') {
          setStatus(next);
          setMessage(next === 'failed' ? 'Le paiement n’a pas été validé.' : 'Le paiement a été annulé.');
          return;
        }
        tries += 1;
        setStatus('pending');
        setMessage('Paiement reçu. Nous terminons la confirmation…');
        if (tries < 12) timer = window.setTimeout(check, 2500);
        else setMessage('La confirmation prend un peu plus de temps. Vous pouvez vérifier à nouveau.');
      } catch (error) {
        if (cancelled) return;
        tries += 1;
        setStatus('pending');
        setMessage('Votre paiement est encore en cours de confirmation.');
        if (tries < 8) timer = window.setTimeout(check, 3000);
      }
    };

    check();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [intentId, upgrade, attempt]);

  const paid = status === 'paid';
  const failed = status === 'failed' || status === 'cancelled' || status === 'missing';

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2">
          <img src={LOGO_URL} alt="LoyerBook" className="h-14 w-14" />
          <div className="text-2xl font-black text-slate-900">Loyer<span className="text-blue-600">Book</span></div>
        </Link>

        <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-2xl shadow-slate-900/10">
          <div className={`px-6 py-10 text-center text-white ${paid ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : failed ? 'bg-gradient-to-r from-rose-500 to-red-500' : 'bg-gradient-to-r from-blue-600 to-indigo-600'}`}>
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-white/20 ring-8 ring-white/10">
              {paid ? <CheckCircle2 className="h-11 w-11" /> : failed ? <XCircle className="h-11 w-11" /> : <Clock3 className="h-11 w-11" />}
            </div>
            <h1 className="mt-5 text-3xl font-black">
              {paid ? (upgrade ? 'Abonnement renouvelé' : 'Bienvenue sur LoyerBook') : failed ? 'Paiement non confirmé' : 'Validation du paiement'}
            </h1>
            <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-white/85">{intentId ? message : 'Nous n’avons pas retrouvé ce paiement. Revenez à votre abonnement pour réessayer.'}</p>
          </div>

          <div className="p-6 sm:p-9">
            <div className="rounded-2xl bg-slate-50 p-5">
              <ShieldCheck className="h-6 w-6 text-emerald-600" />
              <div className="mt-2 font-bold text-slate-900">Votre abonnement est activé seulement après confirmation du paiement</div>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">Cela évite les erreurs et garantit que le bon montant a bien été payé avant l’activation ou le renouvellement.</p>
            </div>

            {intentId && <div className="mt-5 rounded-xl border border-slate-200 bg-white p-3 text-center text-[11px] text-slate-400">Référence de paiement : {intentId}</div>}

            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center">
              {paid ? (
                <Button asChild className="bg-blue-600 hover:bg-blue-700">
                  <Link to={upgrade ? '/bailleur/abonnement' : '/connexion/bailleur'}><LogIn className="mr-2 h-4 w-4" />{upgrade ? 'Voir mon abonnement' : 'Me connecter'}</Link>
                </Button>
              ) : intentId ? (
                <Button onClick={() => setAttempt((value) => value + 1)} className="bg-blue-600 hover:bg-blue-700">
                  <RefreshCw className="mr-2 h-4 w-4" /> Vérifier à nouveau
                </Button>
              ) : (
                <Button asChild className="bg-blue-600 hover:bg-blue-700"><Link to="/bailleur/abonnement">Retour à mon abonnement</Link></Button>
              )}
              <Button asChild variant="outline"><Link to="/"><Home className="mr-2 h-4 w-4" />Retour à l’accueil</Link></Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}